import { AssignedLabsBanner } from '../components/lab/AssignedLabsBanner';
import { ContinueHero } from '../components/trainee/ContinueHero';
import { useMyStats } from '../hooks/useLeaderboard';
import { Award, Target as TargetIcon, TrendingUp, Trophy } from 'lucide-react';
import { useMyAnnouncements } from '../hooks/useAnnouncements';
import { Megaphone } from 'lucide-react';
import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { Crosshair, Map as MapIcon, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  PageContainer,
  PageHeader,
  Section,
  Skeleton,
} from '../components/ui';
import { cardItem, rowItem, staggerContainer } from '../lib/motion';
import { TierBadge } from '../components/journey/TierBadge';
import { RoadmapItemCard } from '../components/journey/RoadmapItemCard';
import { skillLabel } from '../components/journey/tier';
import { useGrantAccess, useMyTiers, useRoadmap } from '../hooks/useJourney';
import { useRanges } from '../hooks/useRanges';
import { selectRole, useAuthStore } from '../store/useAuthStore';
import { useMyExperience } from '../hooks/useExperience';
import { Citadel } from '../components/citadel/Citadel';
import type { RangeStatus, RangeSummary, RoadmapItem, SkillTier, TierLevel } from '../api/types';

export const Route = createFileRoute('/trainee/')({
  component: TraineeHome,
});

/**
 * Trainee landing gate — a trainee whose org's superadmin picked the 'fortress'
 * experience gets the gamified operator console (full-bleed). Everyone else,
 * and all instructors/admins, get the standard roadmap dashboard below.
 */
function TraineeHome() {
  const role = useAuthStore(selectRole);
  const experience = useMyExperience();
  // Trainees may be on the Citadel experience. While we don't yet know which,
  // paint a dark placeholder (the Citadel ground colour) rather than flashing
  // the default dashboard and then swapping to Citadel a moment later.
  if (role === 'trainee') {
    if (experience.isPending) {
      return <div style={{ position: 'fixed', inset: 0, background: '#070b12', zIndex: 60 }} aria-hidden />;
    }
    if (experience.data === 'fortress') {
      return <Citadel />;
    }
  }
  return <TraineeRoadmap />;
}

/**
 * Trainee roadmap dashboard — the home for a placed (or awaiting-placement)
 * trainee. Shows the journey state with a non-blocking 'Take placement' CTA,
 * per-skill tier badges, and tier-ordered recommended content with a Lock
 * affordance on above-tier (gated) items. Instructors viewing this screen get
 * a per-item grant-access override (calls useGrantAccess).
 */
export function TraineeRoadmap() {
  const navigate = useNavigate();
  const role = useAuthStore(selectRole);
  const isInstructor = role === 'instructor' || role === 'org_admin' || role === 'superadmin';

  const tiersQ = useMyTiers();
  const roadmapQ = useRoadmap();
  const rangesQ = useRanges();
  const grant = useGrantAccess();
  const [grantingSlug, setGrantingSlug] = useState<string | null>(null);

  const journeyState = tiersQ.data?.journey.state;
  const awaiting = journeyState === 'awaiting_placement';
  const userId = tiersQ.data?.userId;

  const tiers = useMemo(
    () =>
      [...(tiersQ.data?.tiers ?? [])].sort(
        (a, b) => b.highestUnlockedTierLevel - a.highestUnlockedTierLevel,
      ),
    [tiersQ.data],
  );

  const items = roadmapQ.data?.items ?? [];
  const ranges = rangesQ.data ?? [];

  async function onGrant(item: RoadmapItem, reason: string) {
    if (userId === undefined) return;
    setGrantingSlug(item.contentSlug);
    try {
      await grant.mutateAsync({
        userId,
        contentType: item.contentType,
        contentSlug: item.contentSlug,
        ...(reason ? { reason } : {}),
      });
    } finally {
      setGrantingSlug(null);
    }
  }

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={Crosshair}
        title="My Dashboard"
        description="Your assigned labs and training progress. Everything your instructor has assigned shows up here."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Dashboard' }]}
        {...(awaiting
          ? {
              actions: (
                <Button variant="primary" onClick={() => navigate({ to: '/journey/placement' })}>
                  Take placement
                </Button>
              ),
            }
          : {})}
      />

      <ContinueHero />

      {awaiting ? <PlacementCta onTake={() => navigate({ to: '/journey/placement' })} /> : null}

      <AssignedLabsBanner />

      <TraineeAnnouncements />

      <MyProgress />

      <Section
        title="Active ranges"
        description="Live missions assigned to you. Open the console to jump in."
      >
        {rangesQ.isPending ? (
          <Skeleton lines={2} height={72} gap={12} />
        ) : rangesQ.isError ? (
          <Card variant="danger">
            <Text c="red" size="sm">
              Failed to load your ranges.
            </Text>
          </Card>
        ) : ranges.length === 0 ? (
          <EmptyState
            illustration={<Crosshair size={36} />}
            title="No active missions"
            description="Your instructor hasn't deployed a range for you yet. Assigned ranges show up here when they're live."
          />
        ) : (
          <motion.div variants={staggerContainer} initial="hidden" animate="show">
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
              {ranges.map((r) => (
                <motion.div key={r.id} variants={cardItem}>
                  <MissionCard
                    range={r}
                    onOpen={() => navigate({ to: '/trainee/$rangeId', params: { rangeId: r.id } })}
                  />
                </motion.div>
              ))}
            </SimpleGrid>
          </motion.div>
        )}
      </Section>

      <Section
        title="Skill tiers"
        description="Your competency tier per skill. Above-tier content stays locked until you complete the work that gates it."
      >
        {tiersQ.isPending ? (
          <Skeleton lines={2} height={28} gap={10} />
        ) : tiersQ.isError ? (
          <Card variant="danger">
            <Text c="red" size="sm">
              Failed to load your tiers.
            </Text>
          </Card>
        ) : tiers.length === 0 ? (
          <EmptyState
            illustration={<Sparkles size={36} />}
            title="No tiers yet"
            description="Take the placement quiz to set your starting tier per skill."
            {...(awaiting
              ? { cta: { label: 'Take placement', onClick: () => navigate({ to: '/journey/placement' }) } }
              : {})}
          />
        ) : (
          <motion.div variants={staggerContainer} initial="hidden" animate="show">
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
              {tiers.map((t) => (
                <motion.div key={t.skillSlug} variants={cardItem}>
                  <SkillTierTile tier={t} />
                </motion.div>
              ))}
            </SimpleGrid>
          </motion.div>
        )}
      </Section>

      <Section
        title="Recommended content"
        description="Your tier-ordered path to level up. Locked items unlock as you progress."
      >
        {roadmapQ.isPending ? (
          <Skeleton lines={3} height={48} gap={12} />
        ) : roadmapQ.isError ? (
          <Card variant="danger">
            <Text c="red" size="sm">
              Failed to load your roadmap.
            </Text>
          </Card>
        ) : items.length === 0 ? (
          <EmptyState
            illustration={<MapIcon size={36} />}
            title="No recommended content yet"
            description="Once your instructor tags content to skills and tiers, your path shows up here."
          />
        ) : (
          <motion.div variants={staggerContainer} initial="hidden" animate="show">
            <Stack gap="sm">
              {items.map((item) => (
                <motion.div
                  key={`${item.contentType}:${item.contentSlug}:${item.tierLevel}`}
                  variants={rowItem}
                >
                  <RoadmapItemCard
                    item={item}
                    {...(isInstructor ? { onGrant } : {})}
                    granting={grantingSlug === item.contentSlug && grant.isPending}
                  />
                </motion.div>
              ))}
            </Stack>
          </motion.div>
        )}
      </Section>
    </PageContainer>
  );
}

const RANGE_STATUS_VARIANT: Record<RangeStatus, 'success' | 'info' | 'warn' | 'danger' | 'neutral'> = {
  pending: 'warn',
  provisioning: 'warn',
  ready: 'success',
  attacking: 'info',
  completed: 'success',
  failed: 'danger',
  destroying: 'neutral',
  destroyed: 'neutral',
  hibernated: 'warn',
};

function MissionCard({ range, onOpen }: { range: RangeSummary; onOpen: () => void }) {
  const openable =
    range.status === 'ready' || range.status === 'attacking' || range.status === 'completed';
  const cta =
    range.status === 'provisioning' || range.status === 'pending'
      ? 'Deploying…'
      : openable
        ? 'Open console'
        : 'Unavailable';
  return (
    <Card
      variant="elevated"
      {...(openable ? { onClick: onOpen } : {})}
      style={{ cursor: openable ? 'pointer' : 'default', opacity: openable ? 1 : 0.75 }}
    >
      <Stack gap={8}>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Text fw={600} size="sm" lineClamp={1}>
            {range.scenarioName}
          </Text>
          <Badge variant={RANGE_STATUS_VARIANT[range.status]} size="xs">
            {range.status}
          </Badge>
        </Group>
        <Text size="xs" c="dimmed" lineClamp={1}>
          {range.currentPhase
            ? `Phase: ${range.currentPhase}`
            : `${range.vmCount} VM${range.vmCount === 1 ? '' : 's'}`}
        </Text>
        <Button
          variant="primary"
          disabled={!openable}
          onClick={(e) => {
            e.stopPropagation();
            if (openable) onOpen();
          }}
        >
          {cta}
        </Button>
      </Stack>
    </Card>
  );
}

function PlacementCta({ onTake }: { onTake: () => void }) {
  return (
    <Card variant="elevated" className="mb-6">
      <Group justify="space-between" align="center" wrap="nowrap">
        <Stack gap={2} style={{ flex: 1 }}>
          <Text fw={600} size="sm">
            Set your starting point
          </Text>
          <Text size="xs" c="dimmed">
            You haven&apos;t been placed yet — you&apos;re at Tier 1 across the board.
            Take the placement quiz to unlock content that matches your skills.
          </Text>
        </Stack>
        <Button variant="primary" onClick={onTake}>
          Take placement
        </Button>
      </Group>
    </Card>
  );
}

function SkillTierTile({ tier }: { tier: SkillTier }) {
  const peak: TierLevel = tier.highestUnlockedTierLevel;
  return (
    <Card>
      <Stack gap={6}>
        <Text fw={600} size="sm">
          {skillLabel(tier.skillSlug)}
        </Text>
        <Group gap="xs" justify="space-between">
          <TierBadge level={peak} size="sm" />
          {tier.proficiencyScore !== null ? (
            <Badge variant="neutral" size="xs">
              <span className="font-mono">{Math.round(tier.proficiencyScore)}%</span>
            </Badge>
          ) : null}
        </Group>
      </Stack>
    </Card>
  );
}

function TraineeAnnouncements() {
  const q = useMyAnnouncements();
  if (q.isPending || (q.data?.length ?? 0) === 0) return null;
  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '');
  return (
    <Section title="Announcements" description="Updates from your instructors.">
      <Stack gap="sm">
        {q.data!.slice(0, 5).map((a) => (
          <Card key={a.id}>
            <Stack gap={4}>
              <Group gap="xs" wrap="nowrap">
                <Megaphone size={14} color="rgb(var(--c-accent, 57 217 138))" />
                <Text fw={600} size="sm" c="rgb(var(--c-txt-bright))">{a.title}</Text>
                <Text size="xs" c="dimmed" className="font-mono">{fmt(a.createdAt)}</Text>
              </Group>
              <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>{a.body}</Text>
            </Stack>
          </Card>
        ))}
      </Stack>
    </Section>
  );
}

function MyProgress() {
  const q = useMyStats();
  if (q.isPending || !q.data) return null;
  const d = q.data;
  const metrics: Array<{ label: string; value: string; icon: React.ReactNode }> = [
    { label: 'XP / points', value: String(d.points ?? 0), icon: <Trophy size={16} color="rgb(var(--c-accent, 57 217 138))" /> },
    { label: 'Scenarios completed', value: String(d.scenarios_completed ?? 0), icon: <TargetIcon size={16} /> },
    { label: 'Detection accuracy', value: `${Math.round((d.accuracy ?? 0) * (d.accuracy <= 1 ? 100 : 1))}%`, icon: <TrendingUp size={16} /> },
    { label: 'Badges earned', value: String(d.badges?.length ?? 0), icon: <Award size={16} /> },
  ];
  return (
    <Section title="My progress" description="Your points, completions, and badges across the platform.">
      <Stack gap="md">
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
          {metrics.map((m) => (
            <Card key={m.label}>
              <Stack gap={4}>
                <Group gap={6}>{m.icon}<Text size="xs" c="dimmed" tt="uppercase" fw={600}>{m.label}</Text></Group>
                <Text className="font-mono" fw={700} size="22px" c="rgb(var(--c-txt-bright))">{m.value}</Text>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
        {(d.badges?.length ?? 0) > 0 && (
          <Group gap="xs" wrap="wrap">
            {d.badges!.map((b) => (
              <span key={b.id} title={b.description}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                  borderRadius: 999, background: 'rgba(57,217,138,0.10)', border: '1px solid rgba(57,217,138,0.35)',
                  fontSize: 12, color: 'rgb(var(--c-txt-bright))' }}>
                <Award size={12} /> {b.label}
              </span>
            ))}
          </Group>
        )}
      </Stack>
    </Section>
  );
}
